import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, MessageCircle, Smartphone, Clock, RefreshCw, ExternalLink } from 'lucide-react';
import axios from 'axios';
import AdminHeader from '../components/AdminHeader';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (days > 0) return `${days}d ${hours % 24}h`;
  if (hours > 0) return `${hours}h ${mins % 60}m`;
  return `${mins}m`;
}

function formatSentAt(isoStr) {
  if (!isoStr) return null;
  const d = new Date(isoStr);
  return d.toLocaleString('pt-PT', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
}

// email usa endpoint existente por id; outros usam /api/reminders/:canal/:orderCode
const CHANNELS = [
  { key: 'email',    label: 'Email',    icon: Mail,          color: 'blue',   available: true, useId: true  },
  { key: 'whatsapp', label: 'WhatsApp', icon: MessageCircle, color: 'green',  available: true, useId: false },
  { key: 'sms',      label: 'SMS',      icon: Smartphone,    color: 'orange', available: true, useId: false },
];

const COLOR_MAP = {
  blue:   { sent: 'bg-blue-100 text-blue-700 border-blue-200',   idle: 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100'   },
  green:  { sent: 'bg-green-100 text-green-700 border-green-200', idle: 'bg-green-50 text-green-600 border-green-200 hover:bg-green-100' },
  orange: { sent: 'bg-orange-100 text-orange-700 border-orange-200', idle: 'bg-orange-50 text-orange-600 border-orange-200 hover:bg-orange-100' },
  failed: 'bg-gray-100 text-gray-400 border-gray-200',
};

function ReminderButton({ channel, order, token, onSent, onFailed }) {
  const [loading, setLoading] = useState(false);
  const sentAt = order.remindersSent?.[channel.key];
  const failed = channel.key === 'whatsapp' && order.remindersSent?.whatsappFailed;
  const colors = COLOR_MAP[channel.color];
  const Icon = channel.icon;

  const handleSend = async () => {
    if (!channel.available || sentAt || failed || loading) return;
    if (!window.confirm(`Enviar lembrete por ${channel.label} para ${order.customerName}?`)) return;

    setLoading(true);
    try {
      const url = channel.useId
        ? `${API_URL}/admin/orders/${order.id}/send-reminder`
        : `${API_URL}/reminders/${channel.key}/${order.orderCode}`;
      const res = await axios.post(url, {}, { headers: { Authorization: `Bearer ${token}` } });
      if (res.data.success) onSent(order.orderCode, channel.key, res.data.sentAt || new Date().toISOString());
    } catch (err) {
      if (err.response?.data?.whatsappFailed) {
        onFailed(order.orderCode, 'whatsappFailed');
      }
      alert(err.response?.data?.message || `Erro ao enviar ${channel.label}`);
    } finally {
      setLoading(false);
    }
  };

  if (!channel.available) {
    return (
      <button disabled className={`flex flex-col items-center gap-0.5 px-2.5 py-2 rounded-lg border text-xs font-medium ${colors.soon}`}>
        <Icon className="w-3.5 h-3.5" />
        <span>{channel.label}</span>
        <span className="text-gray-300" style={{ fontSize: 9 }}>em breve</span>
      </button>
    );
  }

  if (failed) {
    return (
      <button disabled className={`flex flex-col items-center gap-0.5 px-2.5 py-2 rounded-lg border text-xs font-medium ${COLOR_MAP.failed}`}>
        <Icon className="w-3.5 h-3.5" />
        <span>{channel.label}</span>
        <span style={{ fontSize: 9 }}>sem WA</span>
      </button>
    );
  }

  if (sentAt) {
    return (
      <button disabled className={`flex flex-col items-center gap-0.5 px-2.5 py-2 rounded-lg border text-xs font-medium ${colors.sent}`}>
        <Icon className="w-3.5 h-3.5" />
        <span>{channel.label}</span>
        <span style={{ fontSize: 9 }}>{formatSentAt(sentAt)}</span>
      </button>
    );
  }

  return (
    <button
      onClick={handleSend}
      disabled={loading}
      className={`flex flex-col items-center gap-0.5 px-2.5 py-2 rounded-lg border text-xs font-medium transition ${colors.idle} ${loading ? 'opacity-60' : ''}`}
    >
      {loading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
      <span>{channel.label}</span>
      <span style={{ fontSize: 9 }}>enviar</span>
    </button>
  );
}

export default function AdminReminders() {
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchOrders = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/reminders`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setOrders(res.data.data);
    } catch (err) {
      if (err.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/backoffice');
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => {
    if (!token) { navigate('/backoffice'); return; }
    fetchOrders();
  }, []);

  const handleSent = (orderCode, channel, sentAt) => {
    setOrders(prev => prev.map(o => {
      if (o.orderCode !== orderCode) return o;
      return { ...o, remindersSent: { ...(o.remindersSent || {}), [channel]: sentAt } };
    }));
  };

  const handleFailed = (orderCode, failedKey) => {
    setOrders(prev => prev.map(o => {
      if (o.orderCode !== orderCode) return o;
      return { ...o, remindersSent: { ...(o.remindersSent || {}), [failedKey]: true } };
    }));
  };

  const getUsername = (order) => {
    if (order.items?.length > 0) return order.items[0].username;
    return order.username;
  };

  const totalSent = orders.reduce((s, o) => {
    const r = o.remindersSent || {};
    return s + Object.values(r).filter(Boolean).length;
  }, 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">A carregar pedidos pendentes...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminHeader activePage="reminders" />

      <div className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h2 className="text-lg font-bold text-gray-900">Lembretes de Pagamento</h2>
            <p className="text-xs text-gray-400">
              {orders.length} pedido{orders.length !== 1 ? 's' : ''} pendente{orders.length !== 1 ? 's' : ''} · {totalSent} lembrete{totalSent !== 1 ? 's' : ''} enviado{totalSent !== 1 ? 's' : ''}
            </p>
          </div>
          <button onClick={fetchOrders} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition px-3 py-1.5 rounded-lg hover:bg-gray-100 border border-gray-200">
            <RefreshCw className="w-4 h-4" />
            Atualizar
          </button>
        </div>

        {orders.length === 0 ? (
          <div className="bg-white rounded-xl p-12 text-center shadow-sm border border-gray-100">
            <p className="text-4xl mb-3">🎉</p>
            <p className="text-lg font-bold text-gray-700">Sem pedidos pendentes</p>
            <p className="text-sm text-gray-400 mt-1">Todos os pedidos estão pagos</p>
          </div>
        ) : (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full min-w-[800px]">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Pedido</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Cliente</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Contacto</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">@Username</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Valor</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">
                      <div className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" />Há</div>
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Canais</th>
                    <th className="px-4 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {orders.map(order => {
                    const username = getUsername(order);
                    const hoursAgo = (Date.now() - new Date(order.createdAt).getTime()) / 3600000;
                    const isUrgent = hoursAgo > 24;
                    return (
                      <tr key={order.orderCode} className={`hover:bg-gray-50 transition ${isUrgent ? 'bg-red-50/30' : ''}`}>
                        <td className="px-4 py-3">
                          <span className="font-bold text-gray-800 text-sm">{order.orderCode}</span>
                          {isUrgent && (
                            <span className="ml-2 text-xs bg-red-100 text-red-600 px-1.5 py-0.5 rounded-full font-semibold">+24h</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <p className="text-sm font-semibold text-gray-800 truncate max-w-[140px]">{order.customerName}</p>
                          <p className="text-xs text-gray-400 truncate max-w-[140px]">{order.customerEmail}</p>
                        </td>
                        <td className="px-4 py-3">
                          {order.customerPhone && (
                            <p className="text-sm text-gray-700 font-medium">{order.customerPhone}</p>
                          )}
                          {order.mbwayPhone && order.mbwayPhone !== order.customerPhone && (
                            <p className="text-xs text-blue-500">MB Way: {order.mbwayPhone}</p>
                          )}
                          {!order.customerPhone && !order.mbwayPhone && (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          {username ? (
                            <span className="text-sm text-gray-700 font-medium">@{username}</span>
                          ) : (
                            <span className="text-xs text-gray-300">—</span>
                          )}
                        </td>
                        <td className="px-4 py-3">
                          <span className="text-sm font-bold text-gray-900">€{parseFloat(order.price).toFixed(2)}</span>
                          <p className="text-xs text-gray-400 capitalize">{order.paymentMethod === 'mbway' ? 'MB Way' : 'Multibanco'}</p>
                        </td>
                        <td className="px-4 py-3">
                          <span className={`text-sm font-semibold ${isUrgent ? 'text-red-600' : 'text-gray-600'}`}>
                            {timeAgo(order.createdAt)}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {CHANNELS.map(ch => (
                              <ReminderButton
                                key={ch.key}
                                channel={ch}
                                order={order}
                                token={token}
                                onSent={handleSent}
                                onFailed={handleFailed}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => navigate(`/backoffice/order/${order.orderCode}`)}
                            className="text-gray-400 hover:text-purple-600 transition"
                            title="Ver pedido"
                          >
                            <ExternalLink className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Legenda */}
        <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-gray-400">
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-blue-100 border border-blue-200" />
            <span>Enviado — mostra data/hora do envio</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-blue-50 border border-blue-200" />
            <span>Disponível — clique para enviar</span>
          </div>
          <div className="flex items-center gap-1.5">
            <div className="w-3 h-3 rounded bg-gray-100 border border-gray-200" />
            <span>Em breve</span>
          </div>
        </div>
      </div>
    </div>
  );
}
